/**
 * Grading React Query Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { gradingService } from '../services';

const GRADING_KEY = 'grading';
const GRADES_KEY = 'grades';
const PERIODS_KEY = 'periods';
const NOTES_KEY = 'notes';

// Grading Settings Hooks
export function useGradingSettings(classId) {
  return useQuery({
    queryKey: [GRADING_KEY, 'settings', classId],
    queryFn: () => gradingService.getGradingSettings(classId),
    enabled: !!classId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateGradingSettings() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ classId, data }) => gradingService.createGradingSettings(classId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [GRADING_KEY, 'settings', variables.classId] });
    },
  });
}

export function useUpdateGradingSettings() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ classId, data }) => gradingService.updateGradingSettings(classId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [GRADING_KEY, 'settings', variables.classId] });
    },
  });
}

// Grade Hooks
export function useClassGrades(classId, params = {}) {
  return useQuery({
    queryKey: [GRADES_KEY, classId, params],
    queryFn: () => gradingService.getClassGrades(classId, params),
    enabled: !!classId,
    staleTime: 2 * 60 * 1000,
  });
}

export function useStudentGrades(studentId, params = {}) {
  return useQuery({
    queryKey: [GRADES_KEY, 'student', studentId, params],
    queryFn: () => gradingService.getStudentGrades(studentId, params),
    enabled: !!studentId,
    staleTime: 2 * 60 * 1000,
  });
}

export function useCreateGrade() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ classId, data }) => gradingService.createGrade(classId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [GRADES_KEY, variables.classId] });
    },
  });
}

export function useUpdateGrade() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ gradeId, data }) => gradingService.updateGrade(gradeId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [GRADES_KEY] });
    },
  });
}

export function useDeleteGrade() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: gradingService.deleteGrade,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [GRADES_KEY] });
    },
  });
}

// Reporting Period Hooks
export function useReportingPeriods(classId) {
  return useQuery({
    queryKey: [PERIODS_KEY, classId],
    queryFn: () => gradingService.getReportingPeriods(classId),
    enabled: !!classId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateReportingPeriod() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ classId, data }) => gradingService.createReportingPeriod(classId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [PERIODS_KEY, variables.classId] });
    },
  });
}

export function useReleaseGrades() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: gradingService.releaseGrades,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PERIODS_KEY] });
    },
  });
}

// Grade Summary Hooks
export function useGradeSummaries(classId) {
  return useQuery({
    queryKey: [GRADING_KEY, 'summaries', classId],
    queryFn: () => gradingService.getGradeSummaries(classId),
    enabled: !!classId,
    staleTime: 2 * 60 * 1000,
  });
}

// Student Note Hooks
export function useStudentNotes(classId, studentId, params = {}) {
  return useQuery({
    queryKey: [NOTES_KEY, classId, studentId, params],
    queryFn: () => gradingService.getStudentNotes(classId, studentId, params),
    enabled: !!classId && !!studentId,
    staleTime: 2 * 60 * 1000,
  });
}

export function useCreateNote() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ classId, studentId, data }) => 
      gradingService.createNote(classId, studentId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [NOTES_KEY, variables.classId, variables.studentId] });
    },
  });
}

export function useUpdateNote() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ noteId, data }) => gradingService.updateNote(noteId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [NOTES_KEY] });
    },
  });
}

export function useDeleteNote() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: gradingService.deleteNote,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [NOTES_KEY] });
    },
  });
}

export function useFollowUpNotes(classId) {
  return useQuery({
    queryKey: [NOTES_KEY, 'follow-up', classId],
    queryFn: () => gradingService.getFollowUpNotes(classId),
    enabled: !!classId,
    staleTime: 1 * 60 * 1000,
  });
}
